import { convertUTCDateInTimeZone } from "@taabl/common-lib/calculators/date";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Constants } from "../../libs/Constants";
import { getList } from "../../libs/dbFunctions";
import LineChart from "../Charts/LineChart";
import "./stockpricegraph.scss";

const StockPriceGraph = ({ activeCompany, monthsToShow }) => {
  const [stockPrices, setStockPrices] = useState([]);
  const [data, setData] = useState({ labels: [], datasets: [] });
  const [options, setOptions] = useState({
    maintainAspectRatio: false,
    tooltips: {
      enabled: false,
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    plugins: {
      datalabels: {
        display: false,
        anchor: "end",
        align: "top",
        font: {
          color: "#423E60",
          weight: 600,
          style: "Montserrat",
        },
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          displayFormats: {
            month: "MMM YY",
          },
          source: "labels",
          parser: "MMM YY",
          unit: "month",
        },
        ticks: {
          fontSize: 11,
          fontFamily: "Montserrat",
          fontColor: "#43425D",
        },
      },
      y: {
        title: {
          display: true,
          text: "$",
          fontSize: 14,
        },
        grid: {
          display: true,
          drawBorder: true,
        },
        ticks: {
          fontSize: 12,
          fontFamily: "Montserrat",
          fontColor: "#43425D",
        },
      },
    },
  });

  useEffect(() => {
    async function load() {
      if (!activeCompany.code) {
        console.log(
          "Company's stock symbol must be provided to query for stock price data."
        );
        return;
      }

      let end = moment.utc().startOf("month");
      let start = moment
        .utc()
        .startOf("month")
        .subtract(monthsToShow, "month");

      let params = {
        stockTickerSymbol: activeCompany.code,
        fromDate: start.format(Constants.DATEFORMAT.YYYYMMDD),
        toDate: end.add(1, "month").format(Constants.DATEFORMAT.YYYYMMDD), // get stock price data up to next month so we get the current month's data
        period: "month",
      };

      try {
        let stockPriceData = await getList(
          Constants.dbTypes.STOCK_PRICES,
          activeCompany,
          params
        );

        // need to compare historical stock price data to months we want to show, since we may be missing some data
        let monthlyClosingPrices = [];
        let currDateLabel = start;
        let nextStockPriceDataIdx = 0;
        for (let i = 0; i <= monthsToShow; i++) {
          // get month and year of label's date so we can compare to stock prices that we know about; then increment month
          let currMonth = currDateLabel.month();
          let currMonthYear = currDateLabel.year();
          currDateLabel = currDateLabel.add(1, "month");

          // if we still have stock price data to check then compare month and year of the next stock price to see if it's a match
          if (nextStockPriceDataIdx < stockPriceData.length) {
            let stockPrice = stockPriceData[nextStockPriceDataIdx];
            let stockPriceDate = moment(stockPrice.date);

            if (
              stockPriceDate.month() === currMonth &&
              stockPriceDate.year() === currMonthYear
            ) {
              // add closing or opening price to the graph if the month and year match (day is irrelevant in this context)
              monthlyClosingPrices.push(
                stockPrice.close ? stockPrice.close : stockPrice.open
              );
              nextStockPriceDataIdx++;
              continue;
            }
          }

          // stock price does not exist in historical data, so we'll populate this month with a 0.
          monthlyClosingPrices.push(null);
        }

        setStockPrices(monthlyClosingPrices);
      } catch (e) {
        toast.error(e);
      }
    }
    load();
  }, [activeCompany]);

  useEffect(() => {
    function recalculateGraphData() {
      let labels = [],
        start = convertUTCDateInTimeZone(
          moment.utc().valueOf(),
          activeCompany.timezone
        )
          .startOf("month")
          .subtract(monthsToShow, "month");
      for (let i = 0; i <= monthsToShow; i++) {
        labels.push(start.clone());
        start = start.add(1, "month");
      }

      let newDatasets = [];
      let gradient = "#fdefee";
      if (document.getElementById("stockpricegraph")) {
        let ctx = document.getElementById("stockpricegraph").getContext("2d");
        if (ctx) {
          gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(253,239,238, 1)");
          gradient.addColorStop(1, "#FFFFFF");
        }
      }
      if (stockPrices && stockPrices.length > 0) {
        newDatasets.push({
          label: "Stock Price",
          data: stockPrices,
          fill: true,
          fillColor: "#fdefee",
          backgroundColor: gradient,
          borderColor: "#f9c4bf",
          lineTension: 0,
          borderWidth: 1,
        });
      }

      setData({ datasets: newDatasets, labels });
      options.scales.x.time.min = labels[0].format("MMM YY");
      options.scales.x.time.max = labels[labels.length - 1].format("MMM YY");
      setOptions({ ...options });
    }
    recalculateGraphData();
  }, [stockPrices]);

  // const chartData = (canvas) => {
  //   console.log('chartData', canvas);
  //   var ctx = canvas.getContext("2d");
  //   var gradient = ctx.createLinearGradient(0, 0, 0, 400);
  //   gradient.addColorStop(0, "rgba(253,239,238, 1)");
  //   gradient.addColorStop(1, "#FFFFFF");
  //   if (data.datasets && data.datasets.length > 0) {
  //     data.datasets[0].backgroundColor = gradient;
  //     data.datasets[0].fillColor = gradient;
  //   }
  //   return { labels: data.labels, datasets: data.datasets };
  // };
  return (
    <div>
      <div className="my-2 font-secondary d-flex justify-content-between">
        <div>
          Market Summary {">"}{" "}
          <b>
            {activeCompany?.name} ({activeCompany?.code})
          </b>
        </div>
      </div>
      <div className="canvas-container">
        <LineChart data={data} options={options} id={"stockpricegraph"} />
        {/* <Line ref={ref} data={chartData} options={options} /> */}
      </div>
    </div>
  );
};

export default StockPriceGraph;
